import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { BookOpen, Plus, Edit, BarChart, Trash2, Eye, EyeOff, Filter } from 'lucide-react';

const YomevCourses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAudience, setFilterAudience] = useState('all');

  useEffect(() => {
    // YÖMEV email kontrolü
    if (!user?.email?.endsWith('@yomev.org')) {
      navigate('/learning');
      return;
    }
    loadCourses();
  }, [user, navigate]);

  const loadCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      }
    } catch (error) {
      console.error('Kurslar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Bu kursu silmek istediğinizden emin misiniz? Tüm bölümler, dersler ve materyaller silinecektir.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/yomev/courses/${courseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('Kurs başarıyla silindi');
        loadCourses();
      } else {
        alert('Kurs silinirken hata oluştu');
      }
    } catch (error) {
      console.error('Kurs silme hatası:', error);
      alert('Kurs silinirken hata oluştu');
    }
  };

  const filteredCourses = courses.filter(course => {
    if (filterStatus !== 'all' && course.status !== filterStatus) return false;
    if (filterAudience !== 'all' && !course.targetAudience.includes(filterAudience)) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const badges = {
      'Taslak': 'bg-gray-100 text-gray-800',
      'Yayında': 'bg-green-100 text-green-800',
      'Arşiv': 'bg-red-100 text-red-800'
    };
    return badges[status] || badges['Taslak'];
  };

  if (!user?.email?.endsWith('@yomev.org')) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Ana Sayfa', path: '/dashboard' },
          { label: 'Online Eğitimlerim', path: null }
        ]} 
      />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">📚 Online Eğitimlerim</h1>
          <p className="text-gray-600">YÖMEV kurs üretim alanı</p>
        </div>
        <button
          onClick={() => navigate('/yomev/courses/new')}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg"
        >
          <Plus size={20} />
          Yeni Kurs Oluştur
        </button>
      </div>

      {/* Filtreler */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter size={20} className="text-gray-500" />
          <div className="flex gap-4 flex-1">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="Taslak">Taslak</option>
              <option value="Yayında">Yayında</option>
              <option value="Arşiv">Arşiv</option>
            </select>

            <select
              value={filterAudience}
              onChange={(e) => setFilterAudience(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tüm Hedef Kitle</option>
              <option value="TEACHER">Öğretmenler</option>
              <option value="COORDINATOR">Koordinatörler</option>
              <option value="PRINCIPAL">Yöneticiler</option>
              <option value="TRAINER">Eğitmenler</option>
            </select>
          </div>
          <span className="text-sm text-gray-600">
            {filteredCourses.length} kurs
          </span>
        </div>
      </div>

      {/* Kurs Listesi */}
      {filteredCourses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Henüz kurs oluşturulmamış
          </h3>
          <p className="text-gray-500 mb-6">
            Başlamak için yukarıdaki "Yeni Kurs Oluştur" butonuna tıklayın
          </p>
          <button
            onClick={() => navigate('/yomev/courses/new')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            İlk Kursunuzu Oluşturun
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCourses.map((course) => (
            <div key={course.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <BookOpen className="text-blue-600" size={24} />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {course.title}
                      </h3>
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(course.status)}`}>
                        {course.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {course.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        👥 Hedef: {course.targetAudience.join(', ') || 'Belirtilmemiş'}
                      </span>
                      <span className="flex items-center gap-1">
                        📊 {course.enrollmentCount} kayıt
                      </span>
                      {course.estimatedDuration && (
                        <span className="flex items-center gap-1">
                          ⏱️ {course.estimatedDuration}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        📅 {new Date(course.createdAt).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => navigate(`/yomev/courses/${course.id}/edit`)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Düzenle"
                    >
                      <Edit size={20} />
                    </button>
                    
                    <button
                      onClick={() => navigate(`/learning/course/${course.id}`)}
                      className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                      title="Önizle"
                    >
                      {course.status === 'Yayında' ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                    
                    <button
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                      title="İstatistikler"
                    >
                      <BarChart size={20} />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(course.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Sil"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default YomevCourses;
